import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Star, ExternalLink, Send, CheckCircle2, Clock, RefreshCw, Sparkles, Link as LinkIcon, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { formatDetail } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewsPage() {
  const [summary, setSummary] = useState({});
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // Google Business Profile state
  const [gStatus, setGStatus] = useState({ connected: false });
  const [reviews, setReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewsError, setReviewsError] = useState("");
  const [discovery, setDiscovery] = useState(null);
  const [replies, setReplies] = useState({}); // review_id -> draft text
  const [savingId, setSavingId] = useState("");
  const [aiBusyId, setAiBusyId] = useState("");
  const [stats, setStats] = useState(null);
  const [autoReply, setAutoReply] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r, g, ar] = await Promise.all([
        api.get("/admin/reviews-summary"),
        api.get("/admin/review-requests"),
        api.get("/admin/google/status"),
        api.get("/admin/google/auto-reply/settings").catch(() => ({ data: { enabled: true } })),
      ]);
      setSummary(s.data); setRequests(r.data.requests || []);
      setGStatus(g.data || { connected: false });
      setAutoReply(!!ar.data?.enabled);
      if (g.data?.connected && g.data?.location_name) loadReviews();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  // Handle ?gconnect=success|error|norefresh from OAuth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const g = params.get("gconnect");
    if (g === "success") { toast.success("Google connected ✓"); window.history.replaceState({}, "", "/admin/reviews"); load(); }
    else if (g === "error") { toast.error("Google connection failed"); window.history.replaceState({}, "", "/admin/reviews"); }
    else if (g === "norefresh") { toast.error("Google did not return a refresh token. Please try again with Prompt=Consent."); window.history.replaceState({}, "", "/admin/reviews"); }
  }, []); // eslint-disable-line

  const loadReviews = async () => {
    setLoadingReviews(true); setReviewsError("");
    try {
      const { data } = await api.get("/admin/google/reviews");
      setReviews(data.reviews || []);
      setStats(data._stats || null);
    } catch (e) {
      setReviewsError(formatDetail(e.response?.data?.detail) || "Failed to load Google reviews");
      setStats(null);
    } finally { setLoadingReviews(false); }
  };

  const toggleAutoReply = async (v) => {
    setAutoReply(v);
    try {
      await api.post("/admin/google/auto-reply/settings", { enabled: v });
      toast.success(`AI auto-reply ${v ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Failed"); setAutoReply(!v); }
  };

  const runAutoReplyNow = async () => {
    toast.info("Scanning new reviews…");
    try {
      await api.post("/admin/google/auto-reply/run-now");
      toast.success("Scan complete — check console log for details");
      await loadReviews();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Failed"); }
  };

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/admin/google/oauth/start");
      window.location.href = data.auth_url;
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Could not start OAuth"); }
  };

  const discoverLocations = async () => {
    try {
      const { data } = await api.post("/admin/google/discover");
      setDiscovery(data);
      if (!data.accounts?.length) toast.error("No GBP accounts found for this user");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Discovery failed"); }
  };

  const saveLocation = async (account_name, location_name, location_title) => {
    try {
      await api.post("/admin/google/config", { account_name, location_name, location_title });
      toast.success("Location saved");
      setDiscovery(null);
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Save failed"); }
  };

  const aiSuggest = async (review) => {
    setAiBusyId(review.reviewId);
    try {
      const { data } = await api.post("/admin/google/reviews/ai-suggest", {
        review_text: review.comment || "",
        star_rating: review.starRating || "",
        reviewer_name: review.reviewer?.displayName || "",
      });
      setReplies((p) => ({ ...p, [review.reviewId]: data.suggestion }));
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "AI failed"); }
    finally { setAiBusyId(""); }
  };

  const sendReply = async (review) => {
    const text = (replies[review.reviewId] || "").trim();
    if (!text) return toast.error("Reply text empty");
    setSavingId(review.reviewId);
    try {
      await api.post(`/admin/google/reviews/${review.reviewId}/reply`, { reply: text });
      toast.success("Reply posted to Google");
      await loadReviews();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Reply failed"); }
    finally { setSavingId(""); }
  };

  const reviewUrl = summary.review_url || "#";

  return (
    <AdminLayout title="Reviews">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Stat label="Total Requests" value={summary.total_requests || 0} icon={<Star className="w-4 h-4" />} />
        <Stat label="Delivered" value={summary.delivered || 0} icon={<CheckCircle2 className="w-4 h-4 text-zen-cal-green" />} />
        <Stat label="Pending SMS" value={summary.pending || 0} icon={<Clock className="w-4 h-4 text-zen-cal-yellow" />} />
        <Stat label="Today" value={summary.today || 0} icon={<Send className="w-4 h-4 text-zen-primary" />} />
      </div>

      {/* ============== GOOGLE BUSINESS PROFILE PANEL ============== */}
      <div className="bg-white rounded-2xl border border-zen-border p-5 mb-6" data-testid="gbp-panel">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <h3 className="font-serif text-lg text-zen-ink">Google Business Profile</h3>
            <div className="text-xs text-zen-muted mt-0.5">Live reviews from your Google listing + AI reply (Gemini Flash).</div>
          </div>
          {(() => {
            // Health-driven status badge + Reconnect button. Owner request: a
            // visible Reconnect button must always appear when the session is
            // expired/invalid OR even when connected, in case the user wants
            // to switch Google accounts.
            const health = gStatus.health || (gStatus.connected ? "connected" : "disconnected");
            const badge = health === "connected" ? { txt: "✓ Connected", cls: "text-emerald-700 bg-emerald-50 border-emerald-300" }
              : health === "expiring_soon" ? { txt: "⏱ Expiring soon", cls: "text-amber-700 bg-amber-50 border-amber-300" }
              : health === "refresh_failed" ? { txt: "⚠ Token refresh failed", cls: "text-red-700 bg-red-50 border-red-300" }
              : { txt: "✗ Disconnected", cls: "text-red-700 bg-red-50 border-red-300" };
            const needsReconnect = health === "disconnected" || health === "refresh_failed";
            return (
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[11px] px-2 py-1 rounded-full border font-medium ${badge.cls}`} data-testid="gbp-health-badge">
                  {badge.txt}
                </span>
                {needsReconnect && (
                  <Button onClick={connectGoogle}
                    className="rounded-full bg-zen-primary hover:bg-zen-primary-hover text-white"
                    data-testid="gbp-reconnect-btn">
                    <LinkIcon className="w-4 h-4 mr-2" /> Reconnect Google
                  </Button>
                )}
                {!needsReconnect && (
                  <>
                    {!gStatus.location_name && (
                      <Button size="sm" variant="outline" onClick={discoverLocations} className="rounded-full" data-testid="gbp-discover-btn">Discover & Pick Location</Button>
                    )}
                    {gStatus.location_name && (
                      <Button size="sm" variant="outline" onClick={loadReviews} className="rounded-full" data-testid="gbp-refresh-btn">
                        <RefreshCw className="w-3 h-3 mr-1" /> Refresh
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={connectGoogle}
                      className="rounded-full text-xs text-zen-muted hover:text-zen-ink"
                      title="Reconnect to switch Google account"
                      data-testid="gbp-switch-account-btn">
                      Switch account
                    </Button>
                  </>
                )}
              </div>
            );
          })()}
        </div>

        {gStatus.connected && gStatus.location_title && (
          <div className="text-xs text-zen-muted mb-3">Location: <span className="text-zen-ink font-medium">{gStatus.location_title}</span></div>
        )}
        {gStatus.last_error && (gStatus.health === "refresh_failed" || gStatus.health === "disconnected") && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3" data-testid="gbp-last-error">
            <span className="font-semibold">Last error:</span> {gStatus.last_error}
            {gStatus.last_refresh_at && (
              <span className="ml-2 text-red-500">· {new Date(gStatus.last_refresh_at).toLocaleString()}</span>
            )}
          </div>
        )}

        {/* Live review stats from Google */}
        {gStatus.connected && gStatus.location_name && stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="gbp-stats">
            <MiniStat label="Total Reviews" value={stats.total} />
            <MiniStat label="Average" value={stats.average ? `${stats.average} ★` : "—"} />
            <MiniStat label="Today" value={stats.today} highlight={stats.today > 0} />
            <MiniStat label="Replied" value={`${stats.with_reply}/${reviews.length}`} />
          </div>
        )}

        {/* AI auto-reply toggle */}
        {gStatus.connected && gStatus.location_name && (
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3 p-3 rounded-xl bg-zen-bg/60 border border-zen-border">
            <div className="text-sm">
              <div className="font-medium text-zen-ink">🤖 AI Auto-Reply (daily at 10 AM)</div>
              <div className="text-xs text-zen-muted">≥3★ → warm thanks · &lt;3★ → apology + (352) 394-1212</div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={runAutoReplyNow} className="rounded-full" data-testid="run-auto-reply-btn">
                Run Now
              </Button>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="checkbox" checked={autoReply}
                  onChange={(e) => toggleAutoReply(e.target.checked)}
                  className="w-4 h-4 accent-zen-primary"
                  data-testid="auto-reply-toggle"
                />
                <span className={autoReply ? "text-zen-cal-green font-medium" : "text-zen-muted"}>{autoReply ? "ON" : "OFF"}</span>
              </label>
            </div>
          </div>
        )}

        {/* Discovery picker */}
        {discovery && (
          <div className="border border-zen-border rounded-xl p-3 mb-3 bg-zen-bg/40" data-testid="gbp-discovery">
            <div className="text-xs uppercase tracking-[0.2em] text-zen-muted mb-2">Pick your salon location</div>
            {discovery.accounts.flatMap((a) => a.locations.map((loc) => (
              <button
                key={loc.location_name}
                onClick={() => saveLocation(a.account_name, loc.location_name, loc.title)}
                className="w-full text-left p-3 rounded-lg border border-zen-border bg-white hover:border-zen-primary mb-2"
                data-testid={`gbp-loc-${loc.location_name}`}
              >
                <div className="font-medium text-zen-ink">{loc.title || loc.location_name}</div>
                <div className="text-xs text-zen-muted">{(loc.address || []).join(", ")}</div>
              </button>
            )))}
          </div>
        )}

        {/* Reviews list */}
        {gStatus.connected && gStatus.location_name && (
          <div className="mt-3">
            {loadingReviews && <div className="text-sm text-zen-muted">Loading reviews…</div>}
            {reviewsError && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700" data-testid="gbp-error">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>{reviewsError}</div>
              </div>
            )}
            {!loadingReviews && !reviewsError && reviews.length === 0 && (
              <div className="text-sm text-zen-muted py-4">No reviews yet, or your Google Business Profile API access is pending.</div>
            )}
            {reviews.map((r) => (
              <ReviewCard
                key={r.reviewId} review={r}
                draft={replies[r.reviewId] || r.reviewReply?.comment || ""}
                onChange={(v) => setReplies((p) => ({ ...p, [r.reviewId]: v }))}
                onAi={() => aiSuggest(r)} aiBusy={aiBusyId === r.reviewId}
                onSend={() => sendReply(r)} saving={savingId === r.reviewId}
              />
            ))}
          </div>
        )}

        {!gStatus.connected && (
          <div className="text-xs text-zen-muted bg-zen-bg/60 border border-dashed border-zen-border rounded-lg p-3">
            ⓘ To read & reply to Google reviews here, click <b>Connect Google</b>. Note: Google Business Profile API access must be approved by Google (apply at developers.google.com/my-business — typical wait 1–4 weeks).
          </div>
        )}
      </div>

      {/* ============== REVIEW REQUEST SMS LINK ============== */}
      <div className="bg-white rounded-2xl border border-zen-border p-5 mb-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-zen-muted mb-2">Google Review Link (sent via SMS)</div>
            <a href={reviewUrl} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 text-zen-primary hover:underline font-medium" data-testid="review-link">
              <ExternalLink className="w-4 h-4" />
              <span className="break-all">{reviewUrl}</span>
            </a>
            <div className="text-[11px] text-zen-muted mt-1">Customers receive this link via SMS when you click "Request Review" on a finished booking.</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-zen-border overflow-hidden">
        <div className="px-5 py-3 border-b border-zen-border flex items-center justify-between">
          <h3 className="font-serif text-lg text-zen-ink">Review Requests Sent</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-zen-muted">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="p-12 text-center text-zen-muted">
            <Star className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <div className="text-sm">No review requests yet.</div>
            <div className="text-xs mt-1">Open a finished booking and click "Request Review".</div>
          </div>
        ) : (
          <div className="divide-y divide-zen-border">
            {requests.map((r) => (
              <div key={r.id} className="px-5 py-3 flex items-center gap-4 hover:bg-zen-bg/40" data-testid={`review-req-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-zen-ink truncate">{r.customer_name || r.customer_phone}</div>
                  <div className="text-xs text-zen-muted">{r.customer_phone} · by @{r.requested_by}</div>
                </div>
                <div className="text-xs text-zen-muted whitespace-nowrap hidden sm:block">
                  {new Date(r.requested_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </div>
                <span className={`text-[10px] font-bold px-2 py-1 rounded-full uppercase whitespace-nowrap ${
                  r.delivered ? "bg-zen-cal-green text-white" : "bg-zen-cal-yellow/20 text-zen-cal-yellow"
                }`}>
                  {r.delivered ? "Sent" : "Queued"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function ReviewCard({ review, draft, onChange, onAi, aiBusy, onSend, saving }) {
  const stars = { FIVE: 5, FOUR: 4, THREE: 3, TWO: 2, ONE: 1 }[review.starRating] || 0;
  const hasReply = !!review.reviewReply?.comment;
  return (
    <div className="border border-zen-border rounded-xl p-4 mb-3 bg-zen-bg/30" data-testid={`gbp-review-${review.reviewId}`}>
      <div className="flex items-center justify-between mb-1">
        <div>
          <span className="font-medium text-zen-ink">{review.reviewer?.displayName || "Anonymous"}</span>
          <span className="ml-2 text-sm text-zen-cal-yellow">{"★".repeat(stars)}{"☆".repeat(5 - stars)}</span>
        </div>
        <span className="text-xs text-zen-muted">{review.createTime ? new Date(review.createTime).toLocaleDateString() : ""}</span>
      </div>
      <p className="text-sm text-zen-ink mb-2 whitespace-pre-wrap">{review.comment || <em className="text-zen-muted">(rating only — no text)</em>}</p>
      {hasReply && (
        <div className="text-xs text-zen-cal-green mb-2">✓ Reply sent {review.reviewReply.updateTime ? `· ${new Date(review.reviewReply.updateTime).toLocaleDateString()}` : ""}</div>
      )}
      <Textarea
        value={draft} onChange={(e) => onChange(e.target.value)}
        placeholder="Write a reply…" rows={3}
        className="bg-white border-zen-border rounded-lg text-sm"
        data-testid={`reply-draft-${review.reviewId}`}
      />
      <div className="flex gap-2 mt-2 justify-end">
        <Button size="sm" variant="outline" onClick={onAi} disabled={aiBusy} className="rounded-full" data-testid={`ai-suggest-${review.reviewId}`}>
          <Sparkles className="w-3 h-3 mr-1" /> {aiBusy ? "Thinking…" : "AI Suggest"}
        </Button>
        <Button size="sm" onClick={onSend} disabled={saving || !draft.trim()} className="rounded-full bg-zen-primary hover:bg-zen-primary-hover text-white" data-testid={`send-reply-${review.reviewId}`}>
          <Send className="w-3 h-3 mr-1" /> {saving ? "Sending…" : hasReply ? "Update" : "Send"}
        </Button>
      </div>
    </div>
  );
}

function MiniStat({ label, value, highlight }) {
  return (
    <div className={`rounded-xl border p-3 text-center ${highlight ? "border-zen-primary bg-zen-primary/5" : "border-zen-border bg-white"}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-zen-muted">{label}</div>
      <div className={`text-2xl font-serif mt-1 ${highlight ? "text-zen-primary font-bold" : "text-zen-ink"}`}>{value}</div>
    </div>
  );
}

function Stat({ label, value, icon }) {
  return (
    <div className="bg-white rounded-2xl border border-zen-border p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-[0.2em] text-zen-muted">{label}</div>
        {icon}
      </div>
      <div className="font-serif text-3xl text-zen-ink mt-2">{value}</div>
    </div>
  );
}
